// The visible "Changed" beside a field holding an unsaved change (TGCLOUD-1902,
// rule 3): the cue that is not a colour. ChangedField draws it at the field's
// top right; a field where that corner is taken (a switch at the end of its row)
// draws it here instead, beside its own label, with `showLabel={false}` on its
// ChangedField. Hidden from screen readers: ChangedField's description says it.

import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { changedPalette } from 'src/theme/palette';
import { changedLabelSx } from './changedFieldStyles';

/**
 * @param {object} props
 * @param {boolean} [props.show]  drawn only while true
 * @param {'inline'|'top-end'|'bottom-end'} [props.placement]  'inline' sits in the flow
 * @param {boolean} [props.announce]  read by screen readers too: where nothing
 *   else describes the change (a record added and not saved yet)
 * @param {React.ReactNode} [props.children]  translated text in place of "Changed"
 */
export default function ChangedFieldLabel({
  show = true,
  placement = 'inline',
  announce = false,
  children,
  sx
}) {
  const t = useAutoTranslation();
  const palette = changedPalette(useTheme());
  if (!show) return null;
  const base = changedLabelSx(palette, placement);
  const inline =
    placement === 'inline'
      ? { position: 'static', display: 'inline-block', ml: 1 }
      : null;
  return (
    <Box
      component="span"
      aria-hidden={announce ? undefined : 'true'}
      data-changed-label=""
      sx={[base, inline, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      {children ?? t('Changed')}
    </Box>
  );
}
